import React, { useCallback, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { Add, DragIndicator } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import uuid from 'react-uuid';
import { AccountData, SchedulerConfigurationData } from '../../api';
import { getOrderPages } from '../../utils/AccountUtils';
import { useLanguage } from '../../common/GeneralUtils';
import { localizedLabel } from '../../utils/maskUtils';
import { SchedulerConfigCard } from './SchedulerConfigCard';
import { FIXED_SLOT_MINUTES } from './consts';

type SchedulerConfigsEditorProps = {
  account: AccountData;
  schedulerConfigs: SchedulerConfigurationData[];
  disabled: boolean;
  onChange: (configs: SchedulerConfigurationData[]) => void;
  /** Raised while any config has an unusable area range, so the form can refuse to save. */
  onValidityChange: (isValid: boolean) => void;
};

const DEFAULT_RESOURCE_COUNT = 10;
const DEFAULT_PERSONS_PER_RESOURCE = 4;

type SortableConfigRowProps = {
  id: string;
  disabled: boolean;
  children: React.ReactNode;
};

const SortableConfigRow = ({ id, disabled, children }: SortableConfigRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });

  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} {...attributes}>
      <Grid container alignItems="flex-start" wrap="nowrap" gap={1}>
        <IconButton
          size="small"
          disabled={disabled}
          {...listeners}
          sx={{ cursor: disabled ? 'not-allowed' : 'grab', '&:active': { cursor: 'grabbing' }, touchAction: 'none' }}>
          <DragIndicator />
        </IconButton>
        <Grid flexGrow={1} minWidth={0}>
          {children}
        </Grid>
      </Grid>
    </div>
  );
};

/**
 * Scheduler configs describe a pool of interchangeable resources (tables, benches) that order
 * pages draw from. Saving the first one is what puts the account on the scheduler path and makes
 * the areas day view available, so the empty state says so out loud.
 */
export const SchedulerConfigsEditor = ({
  account,
  schedulerConfigs,
  disabled,
  onChange,
  onValidityChange,
}: SchedulerConfigsEditorProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const [configToDelete, setConfigToDelete] = useState<SchedulerConfigurationData | null>(null);
  // Held in a ref and reported only on change: cards report validity from effects that re-run on
  // every render, so a state write per report would render forever. Only the aggregate matters.
  const invalidConfigIds = useRef(new Set<string>());

  const setConfigValidity = useCallback(
    (configId: string, isValid: boolean) => {
      const invalid = invalidConfigIds.current;
      if (invalid.has(configId) === !isValid) {
        return;
      }
      if (isValid) {
        invalid.delete(configId);
      } else {
        invalid.add(configId);
      }
      onValidityChange(invalid.size === 0);
    },
    [onValidityChange],
  );

  const orderPages = getOrderPages(account);
  const languages = account.customer_facing_widgets_languages ?? ['he'];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      const oldIndex = schedulerConfigs.findIndex((config) => config.id === active.id);
      const newIndex = schedulerConfigs.findIndex((config) => config.id === over.id);
      onChange(arrayMove(schedulerConfigs, oldIndex, newIndex));
    }
  };

  const addConfig = () =>
    onChange([
      ...schedulerConfigs,
      {
        id: uuid(),
        label: { he: '' },
        resource_count: DEFAULT_RESOURCE_COUNT,
        slot_minutes: FIXED_SLOT_MINUTES,
        persons_per_resource: DEFAULT_PERSONS_PER_RESOURCE,
        require_adjacent_resources: false,
        allowed_masks: [],
        areas: [],
      },
    ]);

  return (
    <Grid container flexDirection="column" wrap="nowrap" gap={1} mt={4}>
      <Typography variant="h6">{t('sharedResources.schedulerConfigs.title')}</Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        {t('sharedResources.schedulerConfigs.description')}
      </Typography>

      {schedulerConfigs.length === 0 ? (
        <Paper variant="outlined" sx={{ padding: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('sharedResources.schedulerConfigs.emptyState')}
          </Typography>
          <Button variant="outlined" startIcon={<Add />} disabled={disabled} onClick={addConfig}>
            {t('sharedResources.schedulerConfigs.addConfig')}
          </Button>
        </Paper>
      ) : (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={schedulerConfigs.map((config) => config.id)} strategy={verticalListSortingStrategy}>
              {schedulerConfigs.map((config, index) => (
                <SortableConfigRow key={config.id} id={config.id} disabled={disabled}>
                  <SchedulerConfigCard
                    config={config}
                    orderPages={orderPages}
                    // The backend rejects an order page that belongs to two configs.
                    pagesUsedElsewhere={
                      new Set(
                        schedulerConfigs
                          .filter((other) => other.id !== config.id)
                          .flatMap((other) => other.allowed_masks.map((mask) => mask.order_page_id)),
                      )
                    }
                    languages={languages}
                    language={language}
                    disabled={disabled}
                    onChange={(updated) =>
                      onChange(schedulerConfigs.map((existing, position) => (position === index ? updated : existing)))
                    }
                    onDelete={() => setConfigToDelete(config)}
                    onValidityChange={(isValid) => setConfigValidity(config.id, isValid)}
                  />
                </SortableConfigRow>
              ))}
            </SortableContext>
          </DndContext>
          <Grid>
            <Button size="small" startIcon={<Add />} disabled={disabled} onClick={addConfig}>
              {t('sharedResources.schedulerConfigs.addConfig')}
            </Button>
          </Grid>
        </>
      )}

      {configToDelete && (
        <Dialog open onClose={() => setConfigToDelete(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('sharedResources.schedulerConfigs.deleteConfig')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {t('sharedResources.schedulerConfigs.deleteConfigConfirm', {
                name: localizedLabel(configToDelete.label, language, configToDelete.id),
              })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfigToDelete(null)}>{t('cancel')}</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                onChange(schedulerConfigs.filter((config) => config.id !== configToDelete.id));
                setConfigValidity(configToDelete.id, true);
                setConfigToDelete(null);
              }}>
              {t('sharedResources.schedulerConfigs.deleteConfig')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Grid>
  );
};
